import { AuthUser } from '@/types/auth';

/**
 * Utility functions for extracting user information from authentication data
 * These are used for UI display purposes when backend profile data is not needed
 */

/**
 * Extracts avatar URL from user authentication data
 */
export function getAvatarUrl(user: AuthUser): string | undefined {
  // Direct avatar_url from user_metadata
  if (user.user_metadata?.avatar_url) {
    return user.user_metadata.avatar_url;
  }
  
  // Google OAuth picture
  if (user.user_metadata?.picture) {
    return user.user_metadata.picture;
  }
  
  // Other common avatar fields
  if (user.user_metadata?.avatar) {
    return user.user_metadata.avatar;
  }
  
  // Check Google identity data
  const googleIdentity = user.identities?.find((id: any) => id.provider === 'google');
  if (googleIdentity?.identity_data) {
    try {
      const identityData = typeof googleIdentity.identity_data === 'string' 
        ? JSON.parse(googleIdentity.identity_data) 
        : googleIdentity.identity_data;
      
      if (identityData.picture) {
        return identityData.picture;
      }
    } catch (error) {
      console.error('Error parsing Google identity data:', error);
    }
  }
  
  return undefined;
}

/**
 * Gets user display name from auth data
 */
export function getUserDisplayName(user: AuthUser): string {
  if (!user) return 'User';
  
  const metadata = user.user_metadata || {};
  
  // Try separate first/last name first
  if (metadata.first_name || metadata.last_name) {
    const displayName = `${metadata.first_name || ''} ${metadata.last_name || ''}`.trim();
    if (displayName) return displayName;
  }
  
  // Try full name field
  if (metadata.full_name && typeof metadata.full_name === 'string') {
    return metadata.full_name.trim();
  }
  
  // Try name field
  if (metadata.name && typeof metadata.name === 'string') {
    return metadata.name.trim();
  }
  
  // Fallback to email prefix
  if (user.email && typeof user.email === 'string') {
    const emailPrefix = user.email.split('@')[0];
    if (emailPrefix) {
      return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
    }
  }
  
  return 'User';
}

/**
 * Gets user initials for avatar fallback
 */
export function getUserInitials(user: AuthUser): string {
  if (!user) return 'U';
  
  const metadata = user.user_metadata || {};
  
  // Try separate first/last name first
  if (metadata.first_name || metadata.last_name) {
    const firstInitial = metadata.first_name?.charAt(0)?.toUpperCase() || '';
    const lastInitial = metadata.last_name?.charAt(0)?.toUpperCase() || '';
    const initials = `${firstInitial}${lastInitial}`;
    if (initials.trim()) return initials;
  }
  
  // Try full name
  const fullName = metadata.full_name || metadata.name;
  if (fullName && typeof fullName === 'string') {
    const nameInitials = fullName.split(' ')
      .filter(n => n.length > 0)
      .map((n: string) => n[0].toUpperCase())
      .slice(0, 2)
      .join('');
    if (nameInitials) return nameInitials;
  }
  
  // Fallback to email
  if (user.email && typeof user.email === 'string') {
    const emailInitials = user.email.split('@')[0].slice(0, 2).toUpperCase();
    if (emailInitials) return emailInitials;
  }
  
  return 'U';
}
